import { atom } from "jotai";

import type { Story, StoryTrack } from "@baiki/interfaces";

import { SnackbarItem } from "@/hooks";

import { Storage, toMinimalStory } from "./storage";

// ============================================================================
// UI State Atoms
// ============================================================================

const StoreTitle = atom<string | undefined>(undefined);

/**
 * Snackbar queue for multiple simultaneous notifications
 */
const StoreSnackbarQueue = atom<SnackbarItem[]>([]);

/**
 * Confetti state for celebration animations
 */
interface ConfettiState {
  isActive: boolean;
  timestamp: number;
}

const StoreConfetti = atom<ConfettiState>({
  isActive: false,
  timestamp: 0,
});

export { StoreSnackbarQueue, StoreTitle, StoreConfetti };

// ============================================================================
// Player State - Separated into Runtime and Persisted
// ============================================================================

/**
 * Runtime player state (not persisted)
 * This state is transient and resets when the app reloads
 */
export interface PlayerRuntimeState {
  playing: boolean;
  buffering: boolean;
  currentTime: number;
  duration: number;
}

const RUNTIME_DEFAULTS: PlayerRuntimeState = {
  playing: false,
  buffering: false,
  currentTime: 0,
  duration: 0,
};

/**
 * Persisted player state (via Storage)
 * This state is saved to localStorage and restored on app reload
 */
export interface PlayerPersistedState {
  story: Story | null;
  track: StoryTrack | null;
  loop: boolean;
  playbackRate: number;
}

/**
 * Combined player state interface
 */
export interface PlayerState extends PlayerRuntimeState, PlayerPersistedState {}

/**
 * Runtime player state atom (transient)
 */
export const playerRuntimeAtom = atom<PlayerRuntimeState>(RUNTIME_DEFAULTS);

/**
 * Derived atom that combines runtime state with persisted state
 * Read: Merges runtime state with persisted settings from Storage
 * Write: Updates both runtime and persisted state appropriately
 */
export const playerAtom = atom(
  (get): PlayerState => {
    const runtime = get(playerRuntimeAtom);
    const storagePlayer = get(Storage.player);
    const storageStory = get(Storage.story);

    // Merge runtime with persisted state
    // storageStory is MinimalStory (without text field) but compatible with Story for player use
    return {
      ...runtime,
      story: storageStory as Story | null,
      track: storagePlayer.track,
      loop: storagePlayer.loop,
      playbackRate: storagePlayer.playbackRate,
    };
  },
  (get, set, update: PlayerState | ((prev: PlayerState) => PlayerState)) => {
    const prevState = get(playerAtom);
    const nextState = typeof update === "function" ? update(prevState) : update;

    // Update runtime state
    set(playerRuntimeAtom, {
      playing: nextState.playing,
      buffering: nextState.buffering,
      currentTime: nextState.currentTime,
      duration: nextState.duration,
    });

    // Persist track if changed
    if (nextState.track?.id !== prevState.track?.id) {
      set(Storage.player, (prev) => ({ ...prev, track: nextState.track }));
    }

    // Persist story if changed (store minimal version without text to save space)
    if (nextState.story?.id !== prevState.story?.id) {
      set(Storage.story, toMinimalStory(nextState.story));
    }

    // Persist settings if changed
    if (
      nextState.loop !== prevState.loop ||
      nextState.playbackRate !== prevState.playbackRate
    ) {
      set(Storage.player, (prev) => ({
        ...prev,
        loop: nextState.loop,
        playbackRate: nextState.playbackRate,
      }));
    }
  },
);

// ============================================================================
// Word Synchronization
// ============================================================================

export const currentWordIndexAtom = atom<number>(-1);
